import React, { useEffect, useMemo, useState } from "react";
import "@styles/sections/VideoSection.scss";
import {
  Box,
  Typography,
  Button,
  Dialog,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import GenericSwiper from "@components/ui/GenericSwiper";
import DropdownFilterMenu from "@components/ui/DropdownFilterMenu";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";

import { getVideoList, getRelateBlogList } from "@api/landingApi";
import { getOptionData } from "@api/commonApi";
import {
  videoFilterData,
  genericLatestPopularFilterData,
} from "@data/FilterData";

const VideoSection = () => {
  const { i18n } = useTranslation();
  const isChinese = i18n.language === "hk";
  const isMobile = useMediaQuery("(max-width:425px)");

  // 原始資料與 options
  const [slides, setSlides] = useState([]);
  const [optionsMap, setOptionsMap] = useState({});

  // 選取值（與 BlogSection 對齊：含 latest_most_popular = 0）
  const [selectedValues, setSelectedValues] = useState({});

  // 相關 Blog（影片彈窗內使用）
  const [relateBlogList, setRelateBlogList] = useState([]);

  // Mobile Dialog
  const [mobileOpen, setMobileOpen] = useState(false);

  // 取影片列表
  useEffect(() => {
    (async () => {
      try {
        const res = await getVideoList();
        setSlides(res.data?.Data || []);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  // 取 options（依每個 type 打 getOptionData；靜態用 staticOptions）
  useEffect(() => {
    (async () => {
      const result = {};
      for (const f of videoFilterData) {
        if (f.staticOptions) {
          result[f.type] = f.staticOptions;
          continue;
        }
        try {
          const res = await getOptionData({ type: f.type });
          result[f.type] = res?.data?.Data || [];
        } catch (err) {
          console.error("Failed to fetch", f.type, err);
        }
      }
      setOptionsMap(result);
    })();
  }, []);

  // Dropdown change（與 BlogSection 同風格）
  const handleFilterChange = (type, selectedItem) => {
    const vf =
      videoFilterData.find((f) => f.type === type)?.valueField || "value";
    setSelectedValues((prev) => ({ ...prev, [type]: selectedItem?.[vf] }));
  };

  const handleResetAllFilters = () => {
    const init = {};
    videoFilterData.forEach((f) => (init[f.type] = ""));
    init.latest_most_popular = 0;
    setSelectedValues(init);
  };

  // ✅ 與 BlogSection 相同的欄位映射與過濾邏輯
  const fieldMap = {
    video_product: "product",
    video_theme: "theme",
  };

  const filtered = useMemo(() => {
    // 先過濾
    const filteredCards = slides.filter((item) => {
      return Object.entries(selectedValues).every(([key, value]) => {
        if (!value) return true;
        if (key === "latest_most_popular") return true; // 排序鍵不影響過濾

        const targetField = fieldMap[key];
        if (!targetField) return true;
        if (targetField === "product") {
          const itemThemes = (item.product?.split(",") || []).map((s) =>
            s.trim()
          );
          return itemThemes.includes(String(value));
        }
        if (targetField === "theme") {
          const itemThemes = (item.theme?.split(",") || []).map((s) =>
            s.trim()
          );
          return itemThemes.includes(String(value));
        }
        return String(item[targetField]) === String(value);
      });
    });

    // 再排序（1=熱門 → view_no；其他=最新 → id）
    const sortValue = Number(selectedValues.latest_most_popular);
    const sorted = [...filteredCards].sort((a, b) => {
      if (sortValue === 1) {
        return (b.view_no || 0) - (a.view_no || 0);
      }
      return Number(b.id) - Number(a.id);
    });

    return sorted;
  }, [slides, selectedValues]);

  // Map 成 GenericSwiper slides
  const videoSlides = useMemo(
    () =>
      filtered.map((s) => ({
        ...s,
        id: s.id,
        image: s.thumbnail_hk
          ? isChinese
            ? s.thumbnail_hk
            : s.thumbnail_hk_en
          : s.thumbnail || s.image,
        title: isChinese ? s.title_hk || s.title : s.title_hk_en || s.title,
        description: isChinese
          ? s.desc_hk || s.description
          : s.desc_hk_en || s.description,
        url: s?.url,
        hashtag: s?.hashtag,
      })),
    [filtered, isChinese]
  );

  // 打開影片時，拉相關 Blog
  const fetchRelateBlogByHashtag = async (hashtag) => {
    setRelateBlogList([]);
    if (!hashtag) return;
    try {
      const res = await getRelateBlogList({ hashtag });
      setRelateBlogList(res.data?.Data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // 把 blog list map 成 GenericSwiper 的 slides
  const mapBlogSlides = (list) =>
    list.map((s) => ({
      ...s,
      id: s.id,
      image: s.thumbnail_hk
        ? isChinese
          ? s.thumbnail_hk
          : s.thumbnail_hk_en
        : s.thumbnail || s.image,
      title: isChinese ? s.title_hk || s.title : s.title_hk_en || s.title,
      description: isChinese
        ? s.desc_hk || s.description
        : s.desc_hk_en || s.description,
      url: s?.url,
    }));

  return (
    <Box className="video-section-container">
      <Typography variant="h4" className="title">
        <Trans i18nKey="generic:latest_video" />
      </Typography>

      {/* ====== Filter：Desktop / Tablet（與 BlogSection 對齊） ====== */}
      {!isMobile && (
        <Box className="filter-container">
          {videoFilterData.map((filter) => (
            <DropdownFilterMenu
              key={filter.type}
              options={optionsMap[filter.type] || []}
              displayField={filter.displayField[i18n.language]}
              valueField={filter.valueField}
              lang={i18n.language}
              value={selectedValues[filter.type]}
              onChange={(item) => handleFilterChange(filter.type, item)}
            />
          ))}
          {/* 最新/最熱門 */}
          <DropdownFilterMenu
            options={genericLatestPopularFilterData}
            displayField={(item) =>
              isChinese ? item.desc_hk : item.desc_hk_en
            }
            valueField="value"
            lang={i18n.language}
            value={selectedValues.latest_most_popular}
            onChange={(item) => handleFilterChange("latest_most_popular", item)}
            icon={"sort"}
          />
          <Button
            className="dropdown-button reset-filter-button"
            onClick={handleResetAllFilters}
          >
            <Trans i18nKey="generic:reset" />
            <RestartAltIcon className="reset-icon" />
          </Button>
        </Box>
      )}

      {/* ====== Filter：Mobile Dialog（複製 BlogSection 的互動） ====== */}
      {isMobile && (
        <>
          <Box className="dialog-mobile-filter-button-container">
            <Button
              className="dropdown-button mobile-filter-button"
              onClick={() => setMobileOpen(true)}
            >
              <FilterAltIcon />
              <Trans i18nKey="generic:filter" />
            </Button>
          </Box>

          <Dialog
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            fullScreen
            PaperProps={{ className: "dialog-filter-container" }}
            sx={{
              "& .MuiDialog-paper": {
                position: "fixed",
                bottom: 0,
                maxHeight: "50vh",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                border: "2px solid #f5f5f5",
                background: "#fbfbfb",
              },
            }}
          >
            <Box className="dialog-top-container">
              <Typography className="dialog-title">
                <Trans i18nKey="generic:latest_video" />
              </Typography>
              <CloseIcon onClick={() => setMobileOpen(false)} />
            </Box>

            <Box className="dialog-content">
              {/* 排序 */}
              <Typography className="dialog-text">
                <SortIcon className="dialog-icon" />
                <Trans i18nKey="generic:sort" />
              </Typography>
              <Box className="toggle-button-container">
                <ToggleButtonGroup
                  value={selectedValues.latest_most_popular ?? 0}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleFilterChange("latest_most_popular", {
                        value: newValue,
                      });
                    }
                  }}
                >
                  <ToggleButton value={0}>
                    <Trans i18nKey="generic:most_latest" />
                  </ToggleButton>
                  <ToggleButton value={1}>
                    <Trans i18nKey="generic:most_popular" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box className="dialog-line"></Box>

              {/* 篩選 Selects */}
              <Typography className="dialog-text">
                <FilterAltIcon className="dialog-icon" />
                <Trans i18nKey="generic:filter" />
              </Typography>

              {videoFilterData.map((filter) => {
                const langKey = filter.displayField?.[i18n.language];
                const valueField = filter.valueField || "value";
                const options = optionsMap[filter.type] || [];
                const currentValue =
                  selectedValues?.[filter.type] ??
                  (options.length > 0 ? options[0][valueField] : "");

                return (
                  <Box className="select-container" key={filter.type}>
                    <Select
                      className="select-filter"
                      variant="outlined"
                      value={currentValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const selectedItem =
                          options.find(
                            (o) => String(o?.[valueField]) === String(v)
                          ) || null;
                        handleFilterChange(filter.type, selectedItem);
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: "20vh" } } }}
                    >
                      {options.map((opt) => (
                        <MenuItem key={opt[valueField]} value={opt[valueField]}>
                          {langKey
                            ? opt[langKey]
                            : opt.label || opt.name || String(opt[valueField])}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                );
              })}

              <Box className="dialog-line"></Box>

              <Box className="dialog-button-container">
                <Button
                  className="button dialog-reset-filter-button"
                  onClick={handleResetAllFilters}
                >
                  <Trans i18nKey="generic:reset" />
                  <RestartAltIcon className="reset-icon" />
                </Button>
                <Button
                  className="loading-button"
                  onClick={() => setMobileOpen(false)}
                >
                  <Trans i18nKey="generic:view_results" />
                </Button>
              </Box>
            </Box>
          </Dialog>
        </>
      )}

      {/* ====== Swiper：保留相關 Blog 的彈窗擴充 ====== */}
      {videoSlides.length > 0 ? (
        <GenericSwiper
          type="video"
          slides={videoSlides}
          onVideoOpen={(slide) => {
            if (slide?.hashtag) fetchRelateBlogByHashtag(slide.hashtag);
          }}
          onVideoClose={() => setRelateBlogList([])}
          renderPopupExtras={() =>
            relateBlogList?.length > 0 ? (
              <Box className="related-section">
                <Typography className="related-title">
                  <Trans i18nKey="generic:related_blog" />
                </Typography>
                <Box className="related-carousel">
                  <GenericSwiper
                    type="blog"
                    slides={mapBlogSlides(relateBlogList)}
                  />
                </Box>
              </Box>
            ) : null
          }
        />
      ) : (
        <Typography className="coming-soon-text" variant="h6">
          <Trans i18nKey="home:coming_soon" />
        </Typography>
      )}
    </Box>
  );
};

export default VideoSection;
